/**
 * RET-1269 U9 — what a past-due surface is allowed to say, decided once.
 *
 * The dashboard tile and the Settings screen read the same published block from
 * two different endpoints (`GET /dashboard/{wid}` and the subscription summary),
 * so the rules live here rather than twice: the two screens disagreeing about a
 * customer's debt is the defect this plan exists to remove.
 *
 * Three rules, and all three are about NOT inventing:
 *
 *  1. A date we cannot vouch for is not shown. `null` is a real answer — on
 *     `collection_method: send_invoice` there is no Stripe retry window, so no
 *     cancellation bound is published AT ALL (R7) — and so is a string that
 *     does not parse, or Go's `0001-01-01` zero time arriving where a pointer
 *     was forgotten (KTD4).
 *  2. A date that has already passed is not shown either. "Tu cuenta se cancela
 *     a más tardar el 3 de septiembre", read on the 11th, is the same class of
 *     untruth as the date a month away that this unit removes. The rule is
 *     applied to the retry date for the same reason: the next attempt cannot be
 *     in the past.
 *  3. `amount_remaining` is minor units. It reaches the customer as currency or
 *     not at all.
 *
 * Role is NOT decided here. Who may see the amount is an authorization question
 * that belongs to the surface (`userIsOwner || userIsAdmin`), and folding it
 * into a formatting helper would hide it from review.
 */

/**
 * The published past-due block, narrowed to the fields these rules read.
 *
 * Structural on purpose: `tSubscriptionSummary` (subscription summary) and
 * `tDashboard['subscription']` (dashboard payload) both satisfy it, which is
 * what lets one rule serve both screens. Every field is optional AND nullable,
 * and the two mean the same thing — "I don't know": `null` is the current BFF
 * saying so, `undefined` is a BFF that does not publish the field yet.
 */
export type tPastDueFields = {
  /** Amount owed in MINOR UNITS (cents). */
  amountRemaining?: number | null;
  /** Upper bound of Stripe's retry window; null for manual collection (R3). */
  cancellationDate?: string | null;
  cancellationDateSource?: string | null;
  collectionMethod?: string | null;
  /** The open invoice's due date — the populated one under `send_invoice`. */
  dueDate?: string | null;
  /** When Stripe retries the charge; null under `send_invoice`. */
  nextPaymentAttempt?: string | null;
};

const MINOR_UNITS_PER_UNIT = 100;

/**
 * The first year a published billing date can plausibly carry.
 *
 * Go's zero `time.Time` serialises as `0001-01-01T00:00:00Z`, which parses
 * cleanly and is not nullish — so `??` and `||` both wave it through. The
 * dashboard's own `formatPaymentDate` already guards this way; the guard is
 * repeated here because the same zero value reaches these fields whenever a
 * struct forgets a pointer (KTD4).
 */
const EARLIEST_PLAUSIBLE_YEAR = 2000;

/**
 * A published date, or `null` when it is absent, unparseable, or a zero-value
 * sentinel — WITHOUT the elapsed check.
 *
 * Split out for the ladder's cancellation bound, which is the one consumer that
 * must KEEP a date already in the past: once the bound passes, the rung goes
 * dateless on purpose (`pastDueCancellationPassed`), and it cannot decide that
 * if the date has already been nulled out from under it. It must still refuse
 * `0001-01-01`, which is what a Go struct publishes when it forgets a pointer
 * (KTD4) — so the sentinel rule lives here, in one place, rather than being
 * retyped at the one call site that needs a different elapsed policy.
 *
 * Everything else wants `resolvePublishedDate`, which is this plus the elapsed
 * check.
 */
export function resolvePlausibleDate(options: {
  value?: string | null;
}): Date | null {
  const { value } = options;

  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getUTCFullYear() < EARLIEST_PLAUSIBLE_YEAR) return null;

  return parsed;
}

/**
 * A published date, or `null` when it is absent, unparseable, a zero-value
 * sentinel, or already in the past.
 */
function resolvePublishedDate(options: {
  value?: string | null;
  now: Date;
}): Date | null {
  const { value, now } = options;

  const parsed = resolvePlausibleDate({ value });
  if (parsed === null) return null;
  if (parsed.getTime() < now.getTime()) return null;

  return parsed;
}

/**
 * When the charge will next be attempted.
 *
 * `next_payment_attempt` under `charge_automatically`; `due_date` under
 * `collection_method: send_invoice`, where Stripe never auto-charges and leaves
 * the first field null (R1). Read in that order rather than switched on
 * `collection_method`, so a cohort whose method we misread still shows the date
 * it actually has.
 */
export function resolveRetryDate(options: {
  fields: tPastDueFields;
  now?: Date;
}): Date | null {
  const { fields, now = new Date() } = options;

  return (
    resolvePublishedDate({ value: fields.nextPaymentAttempt, now }) ??
    resolvePublishedDate({ value: fields.dueDate, now })
  );
}

/**
 * The cancellation deadline, as an upper bound we will honour.
 *
 * ONLY the published `cancellation_date`. There is deliberately no fallback:
 * every local derivation — `expiration_date + VITE_CANCELLATION_DAYS`, the
 * period end, a day count — is a date the backend never agreed to, and shipping
 * one is what put "tu cuenta será bloqueada" a month past the truth.
 */
export function resolveCancellationDeadline(options: {
  fields: tPastDueFields;
  now?: Date;
}): Date | null {
  const { fields, now = new Date() } = options;

  return resolvePublishedDate({ value: fields.cancellationDate, now });
}

/**
 * The ONE way a past-due date is written, on every surface that shows one.
 *
 * The dashboard tile frames it in a sentence and the Settings grid shows it
 * bare, because a grid cell whose siblings read "$599.13 MXN" and "Mensual"
 * must not restate its own label. The FRAME may differ per surface; the value
 * may not — the tile, the Settings row and the ladder all read the same
 * published field, and a customer comparing two screens must not find two
 * renderings of it. That is why the formatting lives here and not at three call
 * sites (U14 requires the same value on every surface).
 *
 * `es-MX` long, matching the billing surfaces' existing convention
 * (`conversionFormattedDate`, the cancellation sentence). Like every other date
 * on these screens it is hardcoded Spanish rather than `i18n.language` — a
 * known limitation of the surrounding code, not a decision taken here; changing
 * it is a change for all of them at once, not for the past-due block alone.
 */
export function formatPastDueDate(date: Date): string {
  return date.toLocaleString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * The amount owed as currency, or `null` when there is nothing honest to show.
 *
 * Mirrors `formatPrice` (SubscriptionManager.functions) so the debt reads like
 * every other figure in Settings; not imported from there because this module
 * is also read by the Dashboard and `src/utils` must not depend on a page.
 *
 * A non-positive amount returns null: "Pago pendiente de $0.00" is a sentence
 * no customer should be sent, and the surfaces already have a figureless line.
 */
export function formatAmountOwed(options: {
  amountMinorUnits?: number | null;
  currency?: string | null;
}): string | null {
  const { amountMinorUnits, currency } = options;

  if (
    amountMinorUnits === null ||
    amountMinorUnits === undefined ||
    Number.isNaN(amountMinorUnits) ||
    amountMinorUnits <= 0
  ) {
    return null;
  }

  const amount = (amountMinorUnits / MINOR_UNITS_PER_UNIT).toLocaleString(
    'en-US',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  );

  return currency ? `$${amount} ${currency}` : `$${amount}`;
}
